import { Box, Typography } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1Button } from 'c1ui'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress/C1CircularProgress'
import { C1Dialog, C1DialogActions, C1DialogContent, C1DialogTitle } from 'components/core/dialog/C1Dialog'
import { SelectOneUserSimpleComponent } from 'components/core/search/users'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { AppUserModalStyles } from 'components/pages/app/users/modals/styles'
import { useAlert } from 'hooks/useAlert'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { UserValue } from 'lib/actions/user/index'
import { searchUsers } from 'lib/actions/user/user-search'
import { CancelFunc } from 'lib/cancel'
import { initialUsersState, UsersReducer } from 'lib/reducers/users/index'
import { useClientRPC } from 'lib/rpcts'
import { AppUserResponsiblePartyServiceSetRequest } from 'pbts/c1/api/app/v1/app_user'
import { SearchUsersRequest } from 'pbts/c1/api/user/v1/user'
import { UserSearchClient } from 'pbts/c1/api/user/v1/user.client'
import React, { FC, useCallback, useReducer, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { SetAppUserIdentityApi } from 'redux_toolkit/app-users'

export type SetIdentityModalProps = {
    appUser: AppUserValue
} & ClosableProps

export const SetIdentityModal: FC<SetIdentityModalProps> = ({ close, appUser }) => {
    const toast = useAlert()
    const intl = useIntl()
    const userSearchClient = useClientRPC(UserSearchClient)
    const { mutate: setIdentity } = SetAppUserIdentityApi()
    const [userState, userDispatch] = useReducer(UsersReducer, initialUsersState)
    const [user, setUser] = useState<UserValue | null>(appUser.identityUser ?? null)

    // TODO Move to hooks. We need a new autocomplete component that works with the new hooks.
    const onQueryChangeUsers = useCallback(
        (query: string): CancelFunc => {
            return searchUsers(userDispatch, userSearchClient, SearchUsersRequest.create({ query }))
        },
        [userDispatch, userSearchClient],
    )

    const onSelectedUser = (value: UserValue | null): void => {
        if (!value) {
            return
        }
        setUser(value)
    }

    const handleSave = useCallback((): void => {
        if (!user) {
            toast(
                intl.formatMessage({
                    id: 'app_user_identity_updated',
                    defaultMessage: 'User is required.',
                }),
                'error',
            )
            return
        }

        setIdentity(
            AppUserResponsiblePartyServiceSetRequest.create({
                appId: appUser.appId,
                appUserId: appUser.id,
                userId: user.id,
            }),
        )
            .then(() => {
                toast(
                    intl.formatMessage({
                        id: 'app_user_identity_updated',
                        defaultMessage: 'Account owner set',
                    }),
                    'success',
                )
                close()
            })
            .catch((err) =>
                toast(
                    intl.formatMessage(
                        {
                            id: 'app_user_type_update_error',
                            defaultMessage: 'Error setting account owner. {code}: {msg}',
                        },
                        {
                            code: (err as RpcError).code,
                            msg: (err as RpcError).message,
                        },
                    ),
                    'error',
                ))

        close()
    }, [appUser.appId, appUser.id, close, intl, setIdentity, toast, user])

    // If autofocus is enabled, we focus when userSearchClient is null, so onQueryChangeUsers returns no data.
    // Then we won't populate the list until the input value changes.
    // So only render the autocomplete component once we have a client.
    const selectUserComponent = !userSearchClient ? <C1CircularProgress /> : <SelectOneUserSimpleComponent
        label={intl.formatMessage({
            id: 'component_set_identity_form_select_user',
            defaultMessage: 'Choose a user',
        })}
        placeholder={intl.formatMessage({
            id: 'component_set_identity_form_select_user_placeholder',
            defaultMessage: 'Search for a user',
        })}
        autoFocus
        initialValue={appUser.identityUser}
        resultList={userState.list}
        onQueryChange={onQueryChangeUsers}
        onSelected={onSelectedUser}
    />

    return (
        <C1Dialog open onClose={close}>
            <C1DialogTitle>
                <FormattedMessage id="component_set_identity_form_title" defaultMessage="Set account owner" />
            </C1DialogTitle>
            <C1DialogContent>
                <Typography>
                    <FormattedMessage
                        id="component_set_identity_form_description"
                        defaultMessage="The ConductorOne user who is responsible for this application account."
                    />
                </Typography>
                <Box sx={AppUserModalStyles.content}>
                    {selectUserComponent}
                </Box>
            </C1DialogContent>
            <C1DialogActions>
                <C1Button onClick={close} variant="outlined">
                    <FormattedMessage id="component_set_identity_form_cancel" defaultMessage="Cancel" />
                </C1Button>

                <C1Button onClick={handleSave} color="primary" variant="contained">
                    <FormattedMessage id="component_set_identity_form_submit" defaultMessage="Set account owner" />
                </C1Button>
            </C1DialogActions>
        </C1Dialog>
    )
}
